/**
 * The feature-request board's wire protocol (PLAN-SUPPORT).
 *
 * Shared by the Worker (which writes these responses and enforces every rule
 * below), the board page (which reads them) and the ops console (which moderates
 * them), exactly as `gallery/protocol.ts` and `support/protocol.ts` are. One
 * module, so the three readers cannot drift: the status words on a badge and the
 * status words in a console `<select>` are one array, and they are one array
 * because they are imported rather than because somebody kept two lists in step.
 *
 * ```
 * public   GET  /api/board/requests            browse: sort, q, cursor, votedByMe
 * session  GET  /api/requests                  the caller's own, hidden ones too
 * session  POST /api/requests                  ask for something
 * session  POST /api/request-votes/:id/vote    one row per (request, account)
 * session  POST /api/request-votes/:id/unvote  take it back
 * admin    GET  /api/admin/board               the queue, hidden rows included
 * admin    POST /api/admin/board/:id/hide      {hidden, reason} — reason required
 * admin    POST /api/admin/board/:id/status    {status, note?}
 * ```
 *
 * ## Three base words, and the reason is the middleware
 *
 * The Worker's session middleware lets a request through when its path starts
 * with one of `PUBLIC_API_PREFIXES` (`worker/index.ts`), so the public read and
 * the writes that need a session cannot share a base path. This is the gallery's
 * own three-way split made again: {@link BOARD_PUBLIC_BASE_PATH} is the browse
 * read a signed-out visitor must reach, {@link REQUESTS_BASE_PATH} is where a
 * named account writes a request, and {@link REQUEST_VOTES_BASE_PATH} is where it
 * writes a vote. The two write paths are different *words* rather than longer
 * forms of the first, so that no amount of `startsWith` on the public prefix can
 * ever reach them.
 *
 * ## A request has no author, on this wire
 *
 * {@link FeatureRequestCard} carries no byline, no display name, no avatar and
 * no user id, and that is a decision rather than an omission (PLAN-SUPPORT, and
 * `migrations/0024_feature_requests.sql` argues it). Feedback should cost nothing
 * socially: there is no profile to claim first, and nobody's name is attached to
 * a request for something they have not worked out yet. The operator sees the
 * author in the console; the board sees the idea. Account deletion therefore
 * anonymises a request rather than deleting it — other brewers voted for it, and
 * it is partly theirs.
 *
 * ## The one place authorship is admitted, and only to its author
 *
 * {@link OwnRequest} is that place. `GET /api/requests` answers the caller with
 * the caller's own rows and nobody else's, which is not a hole in the paragraph
 * above: the anonymity rule is about what *other people* can learn, and telling
 * somebody which requests are theirs tells them something they already knew.
 *
 * What it buys is the surface PLAN-SUPPORT parked for want of one. A request can
 * be hidden with a reason, the reason is stored on the row, and until this route
 * existed there was nowhere to render it — so the author of a hidden request
 * watched it vanish off a public list with no account of why. That is the whole
 * argument for the type carrying {@link OwnRequest.hidden} and
 * {@link OwnRequest.hiddenReason} when no other payload in this protocol does.
 *
 * ## Posting is direct, and moderation is afterwards
 *
 * A signed-in brewer posts and the request is public immediately, exactly as the
 * gallery publishes. The moderation lever is the console's hide-with-a-reason
 * switch after the fact, not an approval queue in front. A solo operator running
 * an approval queue is a board that is always three days stale, which is a worse
 * failure than a row that has to be hidden once a year.
 */

/**
 * Where the public browse read lives. Reachable with no session at all.
 *
 * One route lives here, and it is a read. Everything that writes is under one of
 * the two words below, for the prefix reason in the header.
 */
export const BOARD_PUBLIC_BASE_PATH = '/api/board';

/**
 * Where a request is written. Session-guarded.
 *
 * A different word from `/api/board/`, not a path under it, because
 * `/api/board/` is public by prefix and `POST /api/board/requests` — which is
 * how the route would most naturally read — would have been reachable with no
 * session by construction.
 */
export const REQUESTS_BASE_PATH = '/api/requests';

/**
 * Where a vote is written, and taken back. Session-guarded, and a third word.
 *
 * The same argument as `/api/likes/` (PLAN-GALLERY, 0021): a vote is one row
 * keyed by the caller's own user id and is rendered back to them as a pressed
 * control, so the server has to know whose it was. There is nobody to write it
 * for without a session, so it sits where a session is guaranteed.
 */
export const REQUEST_VOTES_BASE_PATH = '/api/request-votes';

/** Where the console's board lives in the SPA. Linked from the operator's mail. */
export const BOARD_CONSOLE_PATH = '/admin/board';

/**
 * Where the board lives in the SPA: the requests tab of `/support`.
 *
 * A constant for the same reason `SUPPORT_PAGE_PATH` is one — three readers now
 * write this path rather than typing it: the author's status mail, the ticket
 * form's steer towards the board, and the "Yours" section's own link. A tab that
 * moves moves them all at once.
 */
export const BOARD_PAGE_PATH = '/support/requests';

/**
 * How many requests one page of the board carries.
 *
 * 24, the gallery's number, for a list rather than a grid this time: it is a
 * screenful and a half of rows, which is long enough that "Show more" is rarely
 * the first thing somebody has to press and short enough that the first page is
 * one small query.
 */
export const BOARD_PAGE_SIZE = 24;

/**
 * How long a request's title may be.
 *
 * 80 characters: one line in a row, and short enough that writing it is an act
 * of summarising. A title that does not fit is a body, and there is a body.
 */
export const REQUEST_TITLE_MAX_LENGTH = 80;

/**
 * How long the pitch may be.
 *
 * 1000 characters, which is a few paragraphs. Generous, because "what I am
 * trying to do and why the current way does not work" is the useful version of a
 * feature request and it is longer than people assume, and bounded because this
 * is a text column anybody with an account can POST to.
 */
export const REQUEST_BODY_MAX_LENGTH = 1_000;

/**
 * How long the operator's public note may be.
 *
 * 280. It is the sentence a status deserves — "Shipping with the water update" —
 * and it sits under a badge on a row, so anything longer would become the row.
 */
export const REQUEST_NOTE_MAX_LENGTH = 280;

/**
 * Requests one account may have open at once.
 *
 * Ten, and it is a flood guard rather than a policy, exactly as
 * `MAX_OPEN_TICKETS_PER_USER` is. Counted over `status = 'open'` rows
 * **including hidden ones**: hiding is moderation, and moderation must not
 * refund an abuser's quota. A resolved status frees the cap the way a closed
 * ticket frees the ticket cap, so somebody whose ideas are being shipped or
 * declined never runs into it.
 */
export const MAX_OPEN_REQUESTS_PER_USER = 10;

/**
 * Requests one account may post in an hour.
 *
 * Five. Nobody has five well-formed ideas in an hour, and a script has fifty.
 * The window is fixed and the claim is the limit (`worker/board.ts`, cloning
 * `claimImportQuota` from 0022's argument), and it is claimed **after**
 * validation rather than before — deliberately inverting the importer's order,
 * because a refused URL is a probe worth pricing and a refused title is a typo
 * that should not spend somebody's post.
 */
export const BOARD_HOURLY_POST_LIMIT = 5;

/**
 * Where a request stands, from the operator's side.
 *
 * - `open` — we have read it, or we have not yet; either way nothing is decided.
 * - `planned` — we intend to build it.
 * - `shipped` — it is in the product.
 * - `declined` — we are not going to, and the note says why.
 *
 * Four rather than three, because `declined` is the one a board without it lies
 * about: a request nobody will build sits at `open` forever, collecting votes
 * that cannot change the answer. Saying no is the honest use of a public list.
 *
 * Validated in code against this array rather than by a CHECK constraint — see
 * `migrations/0024_feature_requests.sql`, and 0002/0003/0013 for the precedent.
 */
export const FEATURE_REQUEST_STATUSES = ['open', 'planned', 'shipped', 'declined'] as const;

export type FeatureRequestStatus = (typeof FEATURE_REQUEST_STATUSES)[number];

/** Whether a value is a status this build knows. Used by the console's select. */
export function isFeatureRequestStatus(value: unknown): value is FeatureRequestStatus {
  return (
    typeof value === 'string' && (FEATURE_REQUEST_STATUSES as readonly string[]).includes(value)
  );
}

/**
 * How each status reads to a brewer, and to an operator.
 *
 * One map for both surfaces. The board draws these on a badge and the console
 * offers them in a `<select>`, and a product where the operator picks "Planned"
 * and the brewer reads "On the roadmap" is a product with two vocabularies.
 */
export const FEATURE_REQUEST_STATUS_LABELS: Readonly<Record<FeatureRequestStatus, string>> = {
  open: 'Open',
  planned: 'Planned',
  shipped: 'Shipped',
  declined: 'Declined',
};

/**
 * How the board may be ordered. **`top` is the default**, and that is the whole
 * argument for having a board.
 *
 * A board exists to aggregate demand, so the first thing it should show is what
 * is most wanted. It is also the anti-duplicate affordance this version gets in
 * place of merging: somebody about to type a request they think is new meets the
 * popular ones first, and the popular ones are exactly the ones already asked.
 *
 * On a young board everything ties at zero and `top` degenerates to roughly
 * `newest` (the `id DESC` tiebreak makes it exactly that), so the default costs
 * nothing before it starts paying.
 */
export type BoardSort = 'top' | 'newest';

/** Whether a string is a sort this build knows. Anything else reads as `top`. */
export function parseBoardSort(value: unknown): BoardSort {
  return value === 'newest' ? 'newest' : 'top';
}

/**
 * Which status a browse read is asking for, or `null` for all of them.
 *
 * `null` rather than a thrown error for an unrecognised value, and for the same
 * reason {@link parseBoardSort} reads `sort=oldest` as `top`: this is a URL
 * people hand-edit and share, and `status=planed` should show them the board
 * rather than an error page. It is also what a client older than the sections
 * sends — nothing — so the unfiltered read stays the meaning of no parameter.
 */
export function parseBoardStatusFilter(value: unknown): FeatureRequestStatus | null {
  return isFeatureRequestStatus(value) ? value : null;
}

/**
 * Tidy a title, or refuse it.
 *
 * Trimmed, and every internal whitespace run collapsed to one space — a title is
 * one line in a row, and a pasted two-line heading would break the list rather
 * than say anything the first line does not. Empty is a refusal rather than a
 * `null`: a request with no title is not a request.
 *
 * Applied by the browser as it types and by the Worker before it writes, which
 * is why it lives here and not in either of them.
 */
export function checkRequestTitle(
  raw: unknown,
): { readonly ok: true; readonly title: string } | { readonly ok: false } {
  if (typeof raw !== 'string') return { ok: false };
  const title = raw.trim().replace(/\s+/g, ' ');
  if (title.length === 0 || title.length > REQUEST_TITLE_MAX_LENGTH) return { ok: false };
  return { ok: true, title };
}

/**
 * Tidy a body, or refuse it.
 *
 * Trimmed and bounded, and **newlines are kept** — unlike a title and unlike the
 * gallery's blurb. A request is a paragraph or three of argument, the row renders
 * it as prose, and flattening somebody's list of three use cases into one line
 * would be editing their case rather than tidying their input.
 */
export function checkRequestBody(
  raw: unknown,
): { readonly ok: true; readonly body: string } | { readonly ok: false } {
  if (typeof raw !== 'string') return { ok: false };
  const body = raw.trim();
  if (body.length === 0 || body.length > REQUEST_BODY_MAX_LENGTH) return { ok: false };
  return { ok: true, body };
}

/**
 * Tidy the operator's note, or refuse it.
 *
 * Optional in a way the two above are not: absent, `null` and empty all mean "no
 * note", which is the column's NULL and the row's silence. Whitespace runs
 * collapse, because this one really is a single sentence under a badge.
 */
export function checkAdminNote(
  raw: unknown,
): { readonly ok: true; readonly note: string | null } | { readonly ok: false } {
  if (raw === null || raw === undefined) return { ok: true, note: null };
  if (typeof raw !== 'string') return { ok: false };
  const note = raw.trim().replace(/\s+/g, ' ');
  if (note.length === 0) return { ok: true, note: null };
  if (note.length > REQUEST_NOTE_MAX_LENGTH) return { ok: false };
  return { ok: true, note };
}

/**
 * One request, as the board draws it.
 *
 * **There are no author fields on this type, and there never will be.** See the
 * header: the board is anonymous by design, so a byline is not merely absent
 * from this payload, it is absent from the design the payload serves.
 */
export interface FeatureRequestCard {
  readonly id: string;
  readonly title: string;
  readonly body: string;
  /**
   * Where it stands.
   *
   * Typed as the union **or** a bare string, deliberately: a browser holding a
   * cached bundle has to be able to read a Worker that knows a status it does
   * not, and the honest degradation is a badge rendering an unfamiliar word
   * rather than a page that throws. `FEATURE_REQUEST_STATUS_LABELS` is consulted
   * with a fallback to the value itself for exactly that case.
   */
  readonly status: FeatureRequestStatus | string;
  /** The operator's public sentence beside the status, or `null`. */
  readonly adminNote: string | null;
  /** How many accounts have voted for it. One per account, always present. */
  readonly voteCount: number;
  /**
   * Whether the **caller** has voted for it.
   *
   * Always present, and `false` for a signed-out reader rather than absent: the
   * list renders one control either way, and a tri-state would mean every row
   * deciding what an `undefined` means. It is the one field on a public payload
   * that depends on who is asking, which is why the browse response is
   * `Cache-Control: private` — see `worker/board.ts`.
   */
  readonly votedByMe: boolean;
  /** When it was posted, **ms since epoch** (the column is seconds). */
  readonly createdAt: number;
}

/** `GET /api/board/requests`. Keyset pagination: pass `cursor` back to continue. */
export interface BoardPageResponse {
  readonly ok: true;
  readonly requests: readonly FeatureRequestCard[];
  /**
   * The opaque cursor for the next page, or `null` at the end.
   *
   * Keyset rather than an offset, for the gallery's reason: a list people post
   * into moves under a reader's feet, and with `OFFSET` a request posted while
   * somebody is on page one pushes a row they have already read onto page two.
   */
  readonly cursor: string | null;
}

/**
 * How many visible requests stand at each status, for the section headings.
 *
 * Its own read rather than a field on {@link BoardPageResponse}, because the
 * collapsed sections have to say "Shipped (12)" *without* fetching shipped: a
 * count is a property of the board and not of a page of it. Every status is
 * present, zero included, so a heading never has to decide what a missing key
 * meant.
 */
export type BoardCounts = Readonly<Record<FeatureRequestStatus, number>>;

/**
 * `GET /api/board/counts`. Honours `q`, so a search narrows the headings with
 * the list — a collapsed "Shipped (12)" beside a search that matches one of them
 * would be a number about a board the reader is not looking at.
 */
export interface BoardCountsResponse {
  readonly ok: true;
  readonly counts: BoardCounts;
}

/** `POST /api/requests` — the request that was just written. */
export interface FeatureRequestResponse {
  readonly ok: true;
  readonly request: FeatureRequestCard;
}

/**
 * One of the caller's own requests, hidden ones included.
 *
 * Deliberately **not** a `FeatureRequestCard` with two fields bolted on. The
 * card is what a stranger reads and it carries `votedByMe`, which is a control's
 * state on a public list; this is what an author reads about their own row, and
 * the two happen to share four fields rather than one being the other's
 * subtype. Keeping them apart is what stops a future `hiddenReason` finding its
 * way onto the public list because the types were related.
 *
 * There is no `body` here either. The author wrote it, the board shows it, and a
 * "yours" list is a list of things to check the status of rather than a second
 * place to read your own paragraphs back.
 */
export interface OwnRequest {
  readonly id: string;
  readonly title: string;
  /** Where it stands. `| string` for the reason {@link FeatureRequestCard} has it. */
  readonly status: FeatureRequestStatus | string;
  /** The operator's public sentence beside the status, or `null`. */
  readonly adminNote: string | null;
  readonly voteCount: number;
  /** Whether an operator has taken it off the public board. */
  readonly hidden: boolean;
  /**
   * Why it was taken off, or `null`.
   *
   * Required to hide (`worker/admin/routes.ts`) and cleared by un-hiding, so a
   * hidden row always has one and a visible row never does. This field is the
   * whole reason the route exists: a request that disappears with no account of
   * why reads as censorship even when it was a duplicate.
   */
  readonly hiddenReason: string | null;
  /** When it was posted, **ms since epoch** (the column is seconds). */
  readonly createdAt: number;
}

/** `GET /api/requests` — the caller's own, newest first. Never anybody else's. */
export interface OwnRequestsResponse {
  readonly ok: true;
  readonly requests: readonly OwnRequest[];
}

/**
 * What a vote or an unvote answers with: the state, not the change.
 *
 * Both fields are read back out of the row the write just made, in the same
 * transaction, so the browser renders **the server's answer** rather than its own
 * arithmetic. That is what makes the pair safely idempotent from the client's
 * side: voting for something already voted for is a 200 carrying the state that
 * is now true, and a row that optimistically guessed +1 corrects itself against
 * it without refetching the page it is sitting on.
 */
export interface RequestVoteResponse {
  readonly ok: true;
  readonly voteCount: number;
  readonly voted: boolean;
}

/** A refusal from any route here. `error` is a stable code, never prose. */
export interface BoardErrorResponse {
  readonly ok: false;
  readonly error: string;
}

// ---------------------------------------------------------------------------
// Paths
// ---------------------------------------------------------------------------

/** The public browse read, with its query string already built. */
export function boardBrowsePath(query: {
  readonly q?: string;
  readonly sort?: BoardSort;
  readonly cursor?: string | null;
  readonly status?: FeatureRequestStatus | null;
}): string {
  const params = new URLSearchParams();
  if (query.q !== undefined && query.q.trim() !== '') params.set('q', query.q.trim());
  // `top` is the default on both sides, so naming it would only make the URL
  // somebody copies longer than the one they are looking at.
  if (query.sort === 'newest') params.set('sort', 'newest');
  // Named only when asked for. Absent still means every status, which is what
  // the board answered before it had sections and what a shared link may still
  // be asking for.
  if (query.status !== undefined && query.status !== null) params.set('status', query.status);
  if (query.cursor !== undefined && query.cursor !== null) params.set('cursor', query.cursor);
  const search = params.toString();
  return `${BOARD_PUBLIC_BASE_PATH}/requests${search === '' ? '' : `?${search}`}`;
}

/** The counts behind the section headings. Same search, no sort and no cursor. */
export function boardCountsPath(query: { readonly q?: string } = {}): string {
  const params = new URLSearchParams();
  if (query.q !== undefined && query.q.trim() !== '') params.set('q', query.q.trim());
  const search = params.toString();
  return `${BOARD_PUBLIC_BASE_PATH}/counts${search === '' ? '' : `?${search}`}`;
}

/** Vote for a request. Session-guarded; the row it writes is keyed by the caller. */
export function requestVotePath(requestId: string): string {
  return `${REQUEST_VOTES_BASE_PATH}/${encodeURIComponent(requestId)}/vote`;
}

/** Take it back. The same row, deleted — never a negative vote. */
export function requestUnvotePath(requestId: string): string {
  return `${REQUEST_VOTES_BASE_PATH}/${encodeURIComponent(requestId)}/unvote`;
}

/**
 * One request in the console. `POST .../hide` and `POST .../status` write it.
 *
 * `/api/admin/...` written out, as every path the console builds is: the
 * console's base path is module-private in `worker/index.ts` for a runtime reason
 * stated there.
 */
export function adminBoardRequestPath(requestId: string): string {
  return `/api/admin/board/${encodeURIComponent(requestId)}`;
}
